import React, { useMemo, useState, useRef } from 'react';
import { Box, Typography, Stack, Chip, Paper, Tooltip, useTheme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip as ChartTooltip,
  Filler,
  ArcElement,
} from 'chart.js';
import { useDispatch, useSelector } from "react-redux";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import DownloadIcon from "@mui/icons-material/Download";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { downloadCspmReport, clearAllCspmMessages } from "../../../store/cspm/cspmSlice";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  ChartTooltip,
  Filler,
  ArcElement
);

const PASSED_COLOR = "#a5d6a7";
const FAILED_COLOR = "#ef9a9a";
const PASSED_TEXT_COLOR = "#43a047";
const FAILED_TEXT_COLOR = "#e53935";
const HEADER_BG = "#f8f9fa";
const TITLE_COLOR = "#1a2233";
const SUBTITLE_COLOR = "#6b7a90";
const DATE_COLOR = "#757575";
const TREND_COLOR = "#1976d2";
const DIVIDER_COLOR = "#e3eafc";
const CIRCLE_BG = "#e0e0e0"; // light gray
const CIRCLE_PROGRESS = "#1976d2"; // blue

const SEVERITY_COLORS = {
  CRITICAL: '#d32f2f',
  HIGH: '#f44336',
  MEDIUM: '#fbc02d',
  LOW: '#4caf50',
  INFORMATIONAL: '#1976d2',
};
const SEVERITY_BG_COLORS = {
  CRITICAL: '#ffebee',
  HIGH: '#ffebee',
  MEDIUM: '#fff9c4',
  LOW: '#e8f5e9',
  INFORMATIONAL: '#e3f2fd',
};
const SEVERITY_LABELS = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFORMATIONAL'];

// Add percentage formatting helper
function formatPercent(val) {
  if (val == null || isNaN(val)) return '';
  const num = Number(val);
  if (Number.isInteger(num)) return `${num}%`;
  const fixed = num.toFixed(2);
  if (fixed.endsWith('00')) return `${num.toFixed(0)}%`;
  if (fixed.endsWith('0')) return `${num.toFixed(1)}%`;
  return `${fixed}%`;
}

const CSPMHeader = ({ scanObj, trendScore }) => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const {
    isDownloadingCspmReport,
    downloadCspmReportMessage,
    downloadCspmReportError,
  } = useSelector((state) => state.cspm);

  const metrics = useMemo(() => {
    return scanObj?.cspmMetricResult || {
      executedChecks: 0,
      passedChecks: 0,
      failedChecks: 0,
      percentChecks: 0,
      manual: 0,
      uniqueResources: 0,
      failedResources: 0,
      passedResources: 0,
    };
  }, [scanObj]);

  // Trend chart data (last 30 days)
  const trendData = useMemo(() => {
    if (!trendScore) return { labels: [], data: [] };
    const entries = Object.entries(trendScore).sort(
      ([a], [b]) => new Date(a) - new Date(b)
    );
    const last30 = entries.slice(-30);
    return {
      labels: last30.map(([date]) => {
        // Format as MM-DD
        const d = new Date(date);
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${mm}-${dd}`;
      }),
      data: last30.map(([, value]) => value),
    };
  }, [trendScore]);

  // Chart.js data
  const chartData = {
    labels: trendData.labels,
    datasets: [
      {
        label: "Trend Score",
        data: trendData.data,
        fill: true,
        borderColor: TREND_COLOR,
        backgroundColor: "rgba(25, 118, 210, 0.10)",
        pointRadius: 2,
        pointBackgroundColor: TREND_COLOR,
        tension: 0.35,
        borderWidth: 2,
      },
    ],
  };

  // External tooltip state
  const [tooltip, setTooltip] = useState({
    display: false,
    x: 0,
    y: 0,
    title: '',
    value: '',
    date: '',
  });
  const chartRef = useRef();

  // Custom external tooltip handler for Chart.js
  const externalTooltipHandler = (context) => {
    const { chart, tooltip } = context;
    if (!tooltip || !chart) return;
    if (tooltip.opacity === 0) {
      setTooltip((prev) => ({ ...prev, display: false }));
      return;
    }
    // Get canvas position
    const canvasRect = chart.canvas.getBoundingClientRect();
    // Tooltip position relative to page
    const tooltipWidth = 220; // Estimate, adjust as needed
    let x = canvasRect.left + window.scrollX + tooltip.caretX;
    // Smart horizontal positioning
    if (x + tooltipWidth > window.innerWidth - 12) {
      x = window.innerWidth - tooltipWidth - 12; // align right
    } else if (x < 12) {
      x = 12; // align left
    }
    // Place tooltip above or below depending on space
    let y = canvasRect.top + window.scrollY + tooltip.caretY;
    const tooltipHeight = 64; // Estimate, adjust as needed
    if (y > window.innerHeight / 2) {
      y = y - tooltipHeight - 16; // above
    } else {
      y = y + 16; // below
    }
    // Use the date as provided by the chart/labels
    let scanDateLabel = tooltip.title[0];
    // If the label is in YYYY-MM-DD, format as MM-DD
    if (scanDateLabel && scanDateLabel.length >= 10 && scanDateLabel[4] === '-') {
      const d = new Date(scanDateLabel);
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      scanDateLabel = `${mm}-${dd}`;
    }
    // Only show the score as value
    let scoreRaw = tooltip.body[0].lines[0].replace('Compliance: ', '').replace('Trend Score: ', '').trim();
    let score = formatPercent(scoreRaw);
    setTooltip({
      display: true,
      x,
      y,
      title: 'Scan Date',
      date: scanDateLabel,
      value: score,
    });
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false, // Disable default tooltip
        external: externalTooltipHandler,
      },
    },
    scales: {
      x: {
        display: true,
        grid: { color: "#f0f0f0", drawBorder: false },
        ticks: {
          color: SUBTITLE_COLOR,
          font: { size: 10, weight: 500 },
          maxTicksLimit: isMobile ? 4 : isSmallScreen ? 6 : 8,
          padding: 4,
          autoSkip: true,
          maxRotation: 0,
          minRotation: 0,
        },
      },
      y: {
        display: true,
        grid: { display: false },
        ticks: { display: false },
        min: 0,
        max: 100,
      },
    },
    elements: {
      line: { borderWidth: 2 },
      point: { radius: 2 },
    },
  };

  // Progress bar for resources
  const total = metrics.uniqueResources || 0;
  const passed = metrics.passedResources || 0;
  const failed = metrics.failedResources || 0;
  const passedPercent = total > 0 ? (passed / total) * 100 : 0;

  // Calculate severity data for bars
  const severityData = useMemo(() => {
    const result = {};
    if (scanObj?.auditChecks?.Checks) {
      for (const sev of SEVERITY_LABELS) {
        result[sev] = 0;
      }
      scanObj.auditChecks.Checks.forEach(check => {
        const sev = (check.Severity || '').toUpperCase();
        if (SEVERITY_LABELS.includes(sev) && check.Status === 'FAILED') {
          result[sev] += 1;
        }
      });
    }
    return result;
  }, [scanObj]);
  const maxFailed = Math.max(...Object.values(severityData));

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Download file when URL is received
  React.useEffect(() => {
    console.log("🚀 ~ React.useEffect ~ downloadCspmReportMessage:", downloadCspmReportMessage)
    if (downloadCspmReportMessage) {
      
      const link = document.createElement("a");
      link.href = downloadCspmReportMessage;
      link.download = "";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => {
        dispatch(clearAllCspmMessages());
      }, 500);
    }
  }, [downloadCspmReportMessage, dispatch]);

  // Show error snackbar
  React.useEffect(() => {
    if (downloadCspmReportError) {
      setSnackbar({
        open: true,
        message: downloadCspmReportError,
        severity: "error",
      });
      setTimeout(() => {
        dispatch(clearAllCspmMessages());
      }, 4000);
    }
  }, [downloadCspmReportError, dispatch]);

  const handleDownloadReport = () => {
    if (scanObj?.runId) {
      dispatch(downloadCspmReport({ runId: scanObj.runId }));
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 0,
        mb: 2,
        borderRadius: 3,
        background: HEADER_BG,
        minHeight: 0,
        border: "1px solid #e3eafc",
        boxShadow: "0 2px 12px 0 rgba(25, 118, 210, 0.06)",
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "stretch",
            width: "100%",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 2 : 2.5,
            flexWrap: isSmallScreen ? "wrap" : "nowrap",
            minHeight: isMobile ? "auto" : 80,
          }}
        >
          {/* Security Score Section - Blue/Gray Circle, CSPM Title */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 120,
              maxWidth: 180,
              flex: isMobile ? "none" : "0 1 180px",
              py: 0.5,
              gap: 0.5,
            }}
          >
            <Box
              sx={{
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 64,
                height: 64,
                mb: 0.5,
              }}
            >
              {/* Background Circle */}
              <svg width="64" height="64">
                <circle cx="32" cy="32" r="28" stroke={CIRCLE_BG} strokeWidth="6" fill="none" />
              </svg>
              {/* Foreground Circle (progress) */}
              <svg width="64" height="64" style={{ position: 'absolute', top: 0, left: 0 }}>
                <circle
                  cx="32"
                  cy="32"
                  r="28"
                  stroke={CIRCLE_PROGRESS}
                  strokeWidth="6"
                  fill="none"
                  strokeDasharray={2 * Math.PI * 28}
                  strokeDashoffset={2 * Math.PI * 28 * (1 - (metrics.percentChecks / 100))}
                  style={{ transition: 'stroke-dashoffset 0.6s', transform: 'rotate(-90deg)', transformOrigin: '50% 50%' }}
                />
              </svg>
            </Box>
            <Typography
              variant="body2"
              sx={{ color: '#333', fontWeight: 700, fontSize: 15, mb: 0.2, fontFamily: 'inherit' }}
            >
              Security Score
            </Typography>
            <Typography
              variant="h4"
              sx={{
                color: TITLE_COLOR,
                fontWeight: 500,
                fontSize: 24,
                lineHeight: 1.1,
                mt: 0.2,
                fontFamily: 'inherit',
              }}
            >
              {formatPercent(metrics.percentChecks)}
            </Typography>
          </Box>

          {/* Checks by Severity Section */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              minWidth: 220,
              maxWidth: 340,
              flex: isMobile ? 'none' : '1 1 320px',
              py: 0.5,
              gap: 0.5,
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: '#333', fontWeight: 700, fontSize: 15, mb: 0.5, fontFamily: 'inherit' }}
            >
              Checks by Severity
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 0.5, flexWrap: 'nowrap', overflowX: 'auto' }}>
              <Typography variant="body2" sx={{ color: '#888', fontWeight: 600, fontSize: 14, fontFamily: 'inherit', display: 'inline', verticalAlign: 'middle' }}>
                Total: {metrics.executedChecks}
              </Typography>
              <Typography variant="body2" sx={{ color: PASSED_TEXT_COLOR, fontWeight: 600, fontSize: 14, fontFamily: 'inherit', ml: 3, display: 'inline', verticalAlign: 'middle' }}>
                Passed: {metrics.passedChecks}
              </Typography>
              <Typography variant="body2" sx={{ color: FAILED_TEXT_COLOR, fontWeight: 600, fontSize: 14, fontFamily: 'inherit', ml: 3, display: 'inline', verticalAlign: 'middle' }}>
                Failed: {metrics.failedChecks}
              </Typography>
            </Box>
            {/* Bars for each severity */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.7 }}>
              {SEVERITY_LABELS.map(sev => (
                <Box key={sev} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {/* Legend dot: main color */}
                  <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: SEVERITY_COLORS[sev], mr: 1 }} />
                  <Typography variant="body2" sx={{ color: '#6b7a90', fontWeight: 400, fontSize: 13, minWidth: 90, fontFamily: 'inherit', textTransform: 'capitalize' }}>
                    {sev.charAt(0) + sev.slice(1).toLowerCase()}
                  </Typography>
                  {/* Bar: light gray background, main color fill */}
                  <Box sx={{ flex: 1, minWidth: 40, maxWidth: 120, height: 9, bgcolor: '#f5f5f5', borderRadius: 2, overflow: 'hidden', mr: 1 }}>
                    <Box sx={{ width: maxFailed > 0 ? `${(severityData[sev] / maxFailed) * 100}%` : '0%', height: '100%', bgcolor: SEVERITY_COLORS[sev], borderRadius: 2, transition: 'width 0.4s' }} />
                  </Box>
                  <Typography variant="body2" sx={{ color: SEVERITY_COLORS[sev], fontWeight: 500, fontSize: 14, minWidth: 18, textAlign: 'right', fontFamily: 'inherit' }}>
                    {severityData[sev]}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Divider - hidden on small screens */}
          {!isMobile && (
            <Box
              sx={{
                width: "1px",
                background: DIVIDER_COLOR,
                mx: 0.5,
                my: 1,
                alignSelf: "stretch",
                flexShrink: 0,
              }}
            />
          )}

          {/* Resources Section */}
          <Box
            sx={{
              flex: isMobile ? "none" : "0 1 140px",
              minWidth: isMobile ? "100%" : 120,
              maxWidth: isMobile ? "100%" : 180,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              flexShrink: 0,
              py: 0.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
              <Typography
                variant="body2"
                sx={{ color: '#333', fontWeight: 700, fontSize: 15, fontFamily: 'inherit' }}
              >
                Resources Scanned: {total}
              </Typography>
            </Box>
            <Tooltip
              title={`Passed: ${passed} | Failed: ${failed}`}
              arrow
              placement="top"
            >
              <Box
                sx={{
                  position: "relative",
                  height: 7,
                  width: "100%",
                  mb: 0.5,
                }}
              >
                {/* Single progress bar with two colors */}
                <Box
                  sx={{
                    position: "relative",
                    height: "100%",
                    width: "100%",
                    borderRadius: 5,
                    overflow: "hidden",
                    display: "flex",
                    bgcolor: FAILED_COLOR,
                  }}
                >
                  <Box
                    sx={{
                      width: `${passedPercent}%`,
                      height: "100%",
                      bgcolor: PASSED_COLOR,
                    }}
                  />
                </Box>
              </Box>
            </Tooltip>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography
                variant="caption"
                sx={{
                  color: PASSED_TEXT_COLOR,
                  fontWeight: 600,
                  fontSize: "0.68rem",
                }}
              >
                {passed} Passed
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: FAILED_TEXT_COLOR,
                  fontWeight: 600,
                  fontSize: "0.68rem",
                }}
              >
                {failed} Failed
              </Typography>
            </Box>
          </Box>

          {/* Divider - hidden on small screens */}
          {!isMobile && (
            <Box
              sx={{
                width: "1px",
                background: DIVIDER_COLOR,
                mx: 0.5,
                my: 1,
                alignSelf: "stretch",
                flexShrink: 0,
              }}
            />
          )}

          {/* Trend Section - Responsive width, fills available space */}
          <Box
            sx={{
              flex: isMobile ? "none" : "2 1 0",
              minWidth: isMobile ? "100%" : 180,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              py: 0.5,
            }}
          >
            <Box
              sx={{
                width: "100%",
                px: 1,
                py: 0.5,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: '#333', fontWeight: 700, fontSize: 15, mb: 0.2, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%', fontFamily: 'inherit' }}
              >
                Security Score (Last 30 Days)
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  height: 56,
                  overflow: "visible",
                  transition: "box-shadow 0.2s",
                  borderRadius: 2,
                  "&:hover": {
                    boxShadow: "0 4px 16px 0 rgba(25, 118, 210, 0.10)",
                  },
                  position: 'relative',
                }}
              >
                <Line ref={chartRef} data={chartData} options={chartOptions} />
                {/* External Tooltip */}
                {tooltip.display && (
                  <Box
                    sx={{
                      position: 'fixed',
                      left: tooltip.x,
                      top: tooltip.y,
                      zIndex: 1500,
                      pointerEvents: 'none',
                      bgcolor: '#fff',
                      color: '#1a2233',
                      border: '1px solid #e3eafc',
                      borderRadius: 2.5,
                      boxShadow: '0 6px 24px rgba(25, 118, 210, 0.13)',
                      p: 2.2,
                      minWidth: 170,
                      fontSize: 15,
                      fontWeight: 500,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 1.2,
                    }}
                  >
                    {/* Date row as header */}
                    <Typography variant="subtitle2" sx={{ fontSize: 15, color: '#1a2233', fontWeight: 700, mb: 0.5, width: '100%' }}>
                      {tooltip.date}
                    </Typography>
                    {/* Security Score row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: TREND_COLOR, mr: 1 }} />
                      <Typography variant="body2" sx={{ fontSize: 14, color: '#888', fontWeight: 500 }}>
                        Security Score:
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: 15, color: TREND_COLOR, fontWeight: 700, ml: 1 }}>
                        {tooltip.value}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* Divider - hidden on small screens */}
          {!isMobile && (
            <Box
              sx={{
                width: "1px",
                background: DIVIDER_COLOR,
                mx: 0.5,
                my: 1,
                alignSelf: "stretch",
                flexShrink: 0,
              }}
            />
          )}

          {/* Download Button - Fixed size, always right-aligned */}
          {scanObj?.runId && (
            <Box
              sx={{
                flex: isMobile ? "none" : "0 0 56px",
                alignSelf: 'stretch',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                flexShrink: 0,
              }}
            >
              <Tooltip title="Download CSPM Report">
                <span>
                  <IconButton
                    onClick={handleDownloadReport}
                    disabled={isDownloadingCspmReport}
                    sx={{
                      color: "#1976d2",
                      fontSize: isMobile ? 24 : isLargeScreen ? 30 : 28,
                      p: isMobile ? 1 : isLargeScreen ? 1.2 : 1,
                      border: "2px solid #1976d2",
                      background: "rgba(25, 118, 210, 0.08)",
                      "&:hover": { backgroundColor: "rgba(25, 118, 210, 0.18)" },
                      boxShadow: "0 2px 8px 0 rgba(25, 118, 210, 0.10)",
                    }}
                  >
                    {isDownloadingCspmReport ? (
                      <CircularProgress
                        size={isMobile ? 20 : isLargeScreen ? 24 : 22}
                      />
                    ) : (
                      <DownloadIcon
                        sx={{ fontSize: isMobile ? 24 : isLargeScreen ? 28 : 26 }}
                      />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Paper>
  );
};

export default CSPMHeader; 